import axios from "axios";
import config from "../config";

// Chat Room types
export type ChatRoom = {
  id: string;
  name: string;
  character_id: string;
  phase: number;
  created_at: string;
};

export type ChatRooms = {
  chat_rooms: ChatRoom[];
};

export type CreateChatRoomInput = {
  name: string;
  character_id: string;
  llm_model_id: string;
};

export type CreateChatRoomOutput = {
  chat_room_id: string;
};

export type CreateChatRoomAndCharacterRoomInput = {
  name: string;
  character_name: string;
  character_unique_name: string;
  llm_model_id: string;
};

export type CreateChatRoomAndCharacterRoomOutput = {
  chat_room_id: string;
  character_id: string;
};

export type NextPhaseOutput = {
  chat_room_id: string;
  character_id: string;
  phase: number;
};

// Chat Room API functions
export const createChatRoom = async (token: string, data: CreateChatRoomInput): Promise<CreateChatRoomOutput> => {
  const response = await axios.post<CreateChatRoomOutput>(
    `${config.apiUrl}/characters/${data.character_id}/chat-rooms`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const createChatRoomAndCharacterRoom = async (
  token: string,
  data: CreateChatRoomAndCharacterRoomInput,
): Promise<CreateChatRoomAndCharacterRoomOutput> => {
  const response = await axios.post<CreateChatRoomAndCharacterRoomOutput>(`${config.apiUrl}/chat-rooms`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const listChatRoomsByAccountId = async (
  token: string,
  accountId: string,
  characterId: string,
): Promise<ChatRooms> => {
  const response = await axios.get<ChatRooms>(
    `${config.apiUrl}/accounts/${accountId}/characters/${characterId}/chat-rooms`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const listChatRooms = async (token: string, characterId: string): Promise<ChatRooms> => {
  const response = await axios.get<ChatRooms>(`${config.apiUrl}/characters/${characterId}/chat-rooms`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const getChatRoom = async (token: string, chatRoomId: string): Promise<ChatRoom> => {
  const response = await axios.get<ChatRoom>(`${config.apiUrl}/chat-rooms/${chatRoomId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

export const nextPhase = async (token: string, accountId: string, chatRoomId: string): Promise<NextPhaseOutput> => {
  const response = await axios.post<NextPhaseOutput>(
    `${config.apiUrl}/accounts/${accountId}/chat-rooms/${chatRoomId}/next-phase`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

export const joinChatRoom = async (token: string, chatRoomId: string, accountId: string): Promise<void> => {
  await axios.post(
    `${config.apiUrl}/chat-rooms/${chatRoomId}/accounts/${accountId}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const clearMessage = async (token: string, chatRoomId: string): Promise<void> => {
  await axios.put(
    `${config.apiUrl}/chat-rooms/${chatRoomId}/messages/clear`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const rollbackMessage = async (token: string, chatRoomId: string): Promise<void> => {
  await axios.put(
    `${config.apiUrl}/chat-rooms/${chatRoomId}/messages/rollback`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
};

export const exportChatRoom = async (token: string, chatRoomId: string): Promise<void> => {
  await axios.post(
    `${config.apiUrl}/chat-rooms/${chatRoomId}/messages/export`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
};
